import {
  MutableRefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const debounce = (func: () => void, delay: number) => {
  let timeout: ReturnType<typeof setTimeout>;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(func, delay);
  };
};

const useHeightBasedItems = <T>(
  containerRef: MutableRefObject<HTMLDivElement | null>,
  data: T[],
  page: number,
  renderDependencies: any[],
): {
  displayedItems: T[];
  itemsPerPage: number;
  totalPages: number;
  // true while the page map is empty and all items are rendered for measuring;
  // totalPages is a transient 1 during this phase
  isMeasuring: boolean;
} => {
  // { 1: 6, 2: 5, ... } — page number -> item count on that page
  // empty = measuring in progress, render all data to DOM
  const [pageItemsMap, setPageItemsMap] = useState<Record<number, number>>({});
  const prevPageItemMap = useRef<Record<number, number>>({});
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const calculateItems = useCallback(() => {
    if (!containerRef.current) return;
    const containerHeight = containerRef.current.offsetHeight;
    // Hidden (display: none) -> every item measures 0 and would land on one
    // page; stay in measuring state until the ResizeObserver sees it shown
    if (containerHeight === 0) return;
    const children = Array.from(containerRef.current.children) as HTMLElement[];
    if (children.length === 0) return;

    const prevMap = prevPageItemMap.current;
    const prevPageCount = Object.keys(prevMap).length;
    const startPage = prevPageCount || 1;
    const startChildIndex = prevPageCount
      ? Object.values(prevMap).reduce((sum, val) => sum + val, 0)
      : 0;

    const newMap: Record<number, number> = prevPageCount ? { ...prevMap } : {};
    let accumulated = 0;
    let count = 0;
    let currentPage = startPage;

    for (const child of children.slice(startChildIndex)) {
      const childHeight = child.offsetHeight;
      if (count > 0 && accumulated + childHeight > containerHeight) {
        newMap[currentPage] = count;
        currentPage++;
        accumulated = childHeight;
        count = 1;
      } else {
        accumulated += childHeight;
        count++;
      }
    }
    if (count > 0) {
      newMap[currentPage] = count;
    }

    setPageItemsMap(newMap);
    prevPageItemMap.current = newMap;
  }, [containerRef]);

  // When map is empty, DOM has all items → measure them
  useLayoutEffect(() => {
    if (Object.keys(pageItemsMap).length === 0) {
      calculateItems();
    }
  }, [pageItemsMap, calculateItems]);

  // Resize: reset map → triggers re-render with all items → useLayoutEffect measures
  const debouncedTrigger = useMemo(
    () =>
      debounce(() => {
        setPageItemsMap({});
        prevPageItemMap.current = {};
      }, 200),
    [],
  );

  useLayoutEffect(() => {
    window.addEventListener('resize', debouncedTrigger);
    return () => {
      window.removeEventListener('resize', debouncedTrigger);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      observedElementRef.current = null;
    };
  }, [debouncedTrigger]);

  // No deps: the container may mount later (e.g. after a loading state) or be
  // replaced, and a ref change does not trigger a re-render. Re-observe only
  // when the element actually changes.
  const observedElementRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const element = containerRef.current;
    if (element === observedElementRef.current) return;

    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    observedElementRef.current = element;
    if (!element) return;

    resizeObserverRef.current = new ResizeObserver(debouncedTrigger);
    resizeObserverRef.current.observe(element);
  });

  // Data/dependencies change: reset map → recalculates pagination
  useLayoutEffect(() => {
    setPageItemsMap({});
    prevPageItemMap.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ...renderDependencies]);

  const displayedItems = useMemo(() => {
    // Map empty = measuring, render all data so DOM has all children
    if (Object.keys(pageItemsMap).length === 0) return data;

    let startIndex = 0;
    for (let p = 1; p < page; p++) {
      startIndex += pageItemsMap[p] ?? 0;
    }
    const count = pageItemsMap[page] ?? data.length;
    return data.slice(startIndex, startIndex + count);
  }, [data, page, pageItemsMap]);

  const isMeasuring = Object.keys(pageItemsMap).length === 0;
  const itemsPerPage = pageItemsMap[page] ?? data.length;
  const totalPages = Math.max(1, Object.keys(pageItemsMap).length);

  return { displayedItems, itemsPerPage, totalPages, isMeasuring };
};

export default useHeightBasedItems;
